import differenceInDays from 'date-fns/difference_in_calendar_days'
import format from 'date-fns/format'
import isEmpty from 'lodash/isEmpty'
import mapValues from 'lodash/mapValues'
import noop from 'lodash/noop'
import {call, put, select, take, takeLatest} from 'redux-saga/effects'
import {SubmissionError} from 'redux-form/lib/SubmissionError'

import history from '../../history'

import * as api from '../../api/api'
import * as cancelTrack from '../../track/cancelAppointment'
import * as coregTrack from '../../track/coregistration'
import * as crossSelectors from '../../store/cross'
import * as eventsActions from '../../actions/events'
import * as eventsSelectors from '../../store/events'
import * as showroomApptActions from '../../actions/showroomAppointments'
import * as showroomApptSelectors from '../../store/showroomAppointments'
import * as showroomTrack from '../../track/showroomAppointments'
import * as URLS from '../../urls'
import * as userActions from '../../actions/user'
import * as userInteractionActions from '../../actions/userInteractions'
import * as userSelectors from '../../store/user'
import {EventTypeNames, PartnerInfoAnalyticsOrigin, SchedulerRoleEventRoles} from '../../store/enum'
import {getDirections, validateAppointmentForm} from './helpers'

// Get Showroom Services
// ----------------------------------------------------------------------------

export function* getShowroomServicesworker() {
  try {
    yield put(showroomApptActions.getShowroomAppointmentServicesPending())
    const locationId = yield select(showroomApptSelectors.showroomId)
    const response = yield call(api.getMyTimeServices, locationId)

    const {variations} = api.onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not get showroom services: ${statusText}`)
      },
    })(response)

    yield put(showroomApptActions.getShowroomAppointmentServicesSuccess({services: variations}))
  } catch (e) {
    yield put(
      showroomApptActions.getShowroomAppointmentServicesFailure(
        new Error('Could not get showroom services.'),
      ),
    )
  }
}

export function* getShowroomServicesSaga() {
  yield takeLatest(showroomApptActions.GET_SHOWROOM_APPOINTMENT_SERVICES, getShowroomServicesworker)
}

// Get Open Dates
// ----------------------------------------------------------------------------

export function* getOpenDatesWorker({payload}) {
  try {
    yield put(showroomApptActions.getShowroomAppointmentSchedulePending())
    const response = yield call(api.getMyTimeOpenDates, payload.locationId, payload.serviceId)

    const results = api.onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not get showroom open dates: ${statusText}`)
      },
    })(response)

    yield put(showroomApptActions.getShowroomAppointmentScheduleSuccess(results))
  } catch (e) {
    yield put(
      showroomApptActions.getShowroomAppointmentScheduleFailure(
        new Error('Could not get showroom open dates.'),
      ),
    )
  }
}

export function* getOpenDatesSaga() {
  yield takeLatest(showroomApptActions.GET_SHOWROOM_APPOINTMENT_SCHEDULE, getOpenDatesWorker)
}

// Get Open Times
// ----------------------------------------------------------------------------

export function* getOpenTimesWorker({payload: {date, locationId, serviceId}}) {
  try {
    yield put(showroomApptActions.getShowroomOpenTimesPending())

    const response = yield call(api.getMyTimeOpenTimes, locationId, serviceId, date)

    const results = api.onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not get showroom open times: ${statusText}`)
      },
    })(response)

    yield put(showroomApptActions.getShowroomOpenTimesSuccess(results))
  } catch (e) {
    yield put(
      showroomApptActions.getShowroomOpenTimesFailure(
        new Error('Could not get showroom open times.'),
      ),
    )
  }
}

export function* getOpenTimesSaga() {
  yield takeLatest(showroomApptActions.GET_SHOWROOM_OPEN_TIMES, getOpenTimesWorker)
}

// Track Get Showroom Appointments ScheduledSuccessWorker
// ----------------------------------------------------------------------------

export function* trackGetShowroomAppointmentsScheduledSuccessWorker() {
  const currentShowroom = yield select(showroomApptSelectors.showroomSlug)
  const daysToEvent = yield select(eventsSelectors.currentEventDaysAway)
  const appointmentType = yield select(showroomApptSelectors.appointmentType)
  const totalOpenDays = yield select(showroomApptSelectors.openDates)

  const daysToFirstAvailable = differenceInDays(totalOpenDays[0].date, new Date())
  yield call(
    showroomTrack.viewedShowroomCalendar,
    appointmentType,
    daysToEvent,
    daysToFirstAvailable,
    totalOpenDays.length,
    currentShowroom,
  )
}

export function* trackGetShowroomAppointmentsScheduledSuccessSaga() {
  yield takeLatest(
    showroomApptActions.GET_SHOWROOM_APPOINTMENT_SCHEDULE_SUCCESS,
    trackGetShowroomAppointmentsScheduledSuccessWorker,
  )
}

// Appointment Form Handling
// ----------------------------------------------------------------------------

const genericAppointmentFormError =
  'Oops! It looks like something went wrong with our appointment system. Please call customer care at 1 (888) 717-1588'

/**
 * Interfaces with the partner info API for submitting the appointment form.
 *
 * @param {Object} fields The form field values, keyed by field name
 * @returns {Object} Returns the error object if failure, empty object if success
 */
export function* handleUpdatingPartnerInfo(fields) {
  let errors = {}
  yield put(
    eventsActions.updatePartnerInfo({
      email: fields.partnerEmail,
      eventId: fields.eventId,
      firstName: fields.partnerFirstName,
      isEmailOptedIn: true,
      lastName: fields.partnerLastName,
      origin: PartnerInfoAnalyticsOrigin.APPOINTMENT,
    }),
  )
  const {type} = yield take([
    eventsActions.UPDATE_PARTNER_INFO_SUCCESS,
    eventsActions.UPDATE_PARTNER_INFO_FAILURE,
  ])
  if (type === eventsActions.UPDATE_PARTNER_INFO_FAILURE) {
    const apiErrors = yield select(eventsSelectors.updatePartnerInfoRequestErrors)
    if (apiErrors.message) {
      errors = {_error: genericAppointmentFormError}
    } else {
      errors = mapValues(apiErrors, (messages) => messages.shift())
    }
  }
  return errors
}

/**
 * Handles tracking coreg partner info errors across validation attempts
 *
 * @param {Object} fields The fields being submitted via form
 * @param {Object} errors The error object with validation errors
 * @returns {void}
 */
export function* handleTrackingPartnerInfoErrors(fields, errors) {
  if (errors.partnerEmail || errors.partnerFirstName || errors.partnerLastName) {
    yield call(coregTrack.partnerInfoError, {
      email: fields.partnerEmail,
      firstName: fields.partnerFirstName,
      lastName: fields.parterLastName,
      isEmailInvalid: !!errors.partnerEmail,
      isFirstNameInvalid: !!errors.partnerFirstName,
      isLastNameInvalid: !!errors.partnerLastName,
      origin: PartnerInfoAnalyticsOrigin.APPOINTMENT,
    })
  }
}

export function* submitAppointmentFormWorker({
  payload: {fields, resolve, reject, savePartnerInfo = false},
}) {
  const errors = yield call(validateAppointmentForm, fields, savePartnerInfo)
  if (!isEmpty(errors)) {
    yield call(reject, new SubmissionError(errors))
    yield call(handleTrackingPartnerInfoErrors, fields, errors)
    return
  }

  if (savePartnerInfo) {
    const updatePartnerInfoErrors = yield call(handleUpdatingPartnerInfo, fields)
    if (!isEmpty(updatePartnerInfoErrors)) {
      yield call(reject, new SubmissionError(updatePartnerInfoErrors))
      yield call(handleTrackingPartnerInfoErrors, fields, updatePartnerInfoErrors)
      return
    }
  }

  yield put(showroomApptActions.createAppointment({fields}))
  const action = yield take([
    showroomApptActions.CREATE_APPOINTMENT_SUCCESS,
    showroomApptActions.CREATE_APPOINTMENT_FAILURE,
  ])
  if (action.type === showroomApptActions.CREATE_APPOINTMENT_SUCCESS) {
    yield call(resolve)
  } else {
    yield call(reject, new SubmissionError({_error: genericAppointmentFormError}))
  }
}

export function* submitAppointmentFormSaga() {
  yield takeLatest(showroomApptActions.SUBMIT_APPOINTMENT_FORM, submitAppointmentFormWorker)
}

// Create Appointment
// ----------------------------------------------------------------------------

export function* createAppointmentWorker({payload: {fields}}) {
  try {
    yield put(showroomApptActions.createAppointmentPending())

    const {beginAt, dealId, endAt} = yield select(showroomApptSelectors.selectedTimeSlot)
    const daysToEvent = yield select(eventsSelectors.eventDaysAwayById, fields.eventId)
    const isNordstromShowroom = yield select(showroomApptSelectors.isNordstromLocation)
    const showroomSlug = yield select(showroomApptSelectors.showroomSlug)
    const variationName = yield select(showroomApptSelectors.appointmentType)
    const showroomInstructions = getDirections(isNordstromShowroom, showroomSlug)
    const appointmentPayload = {
      beginAt,
      dealId,
      email: fields.emailAddress,
      eventDate: fields.eventDate,
      eventId: fields.eventId,
      eventName: EventTypeNames[fields.eventType],
      eventRole: fields.weddingRole,
      firstName: fields.firstName,
      lastName: fields.lastName,
      locationId: yield select(showroomApptSelectors.showroomId),
      nordstromOptin: fields.emailCheckBox,
      phoneNumber: fields.mobilePhoneNumber,
      showroomInstructions,
      userId: yield select(userSelectors.id),
      variationId: yield select(showroomApptSelectors.appointServiceId),
      template: {
        date: format(beginAt, 'MMMM D, YYYY'),
        startTime: format(beginAt, 'h:mma'),
        endTime: format(endAt, 'h:mma'),
      },
    }

    const response = yield call(api.createAppointment, appointmentPayload)
    api.onStatus({
      OK: noop,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not create appointment: ${statusText}`)
      },
    })(response)

    yield call(
      showroomTrack.submittedAppointmentForm,
      variationName,
      differenceInDays(beginAt, new Date()),
      daysToEvent,
    )

    yield put(showroomApptActions.createAppointmentSuccess({fields}))
  } catch (e) {
    yield put(
      showroomApptActions.createAppointmentFailure(new Error('Could not create appointment.')),
    )
  }
}

export function* createAppointmentSaga() {
  yield takeLatest(showroomApptActions.CREATE_APPOINTMENT, createAppointmentWorker)
}

export function* appointmentCreationSuccessWorker({payload: {fields}}) {
  const isCurrentEventOwner = yield select(crossSelectors.userIsOwner)
  const isEventOwnerForSelectedEvent = yield select(
    showroomApptSelectors.isEventOwnerForSelectedEvent,
  )
  const isSelectedEventInProgress = yield select(showroomApptSelectors.isSelectedEventInProgress)

  try {
    const {eventId, eventDate, eventType, firstName, lastName, weddingRole} = fields
    const isCurrentEvent = yield select(eventsSelectors.isCurrentEvent, eventId)

    // Only allow event owner with a selected or new event to update their contact details
    if (isEventOwnerForSelectedEvent || isCurrentEventOwner) {
      const ownerDetailsResponse = yield call(api.postOwnerDetails, eventId, {
        firstName,
        lastName,
        role: weddingRole && SchedulerRoleEventRoles[weddingRole],
      })

      const ownerDetails = api.onStatus({
        OK: ({json}) => json,
        403: () => {
          throw new Error('CSRF Token Invalid')
        },
        DEFAULT: ({statusText}) => {
          throw new Error(`Could not post owner details: ${statusText}`)
        },
      })(ownerDetailsResponse)

      yield put(eventsActions.updateCurrentOwnerRole(ownerDetails.role))
      yield put(userActions.updateName(ownerDetails.owner.first_name, ownerDetails.owner.last_name))

      // Only allow event details to be updated if the event is in progress or is a new one created
      // in the booking appointment flow
      if (isSelectedEventInProgress || isCurrentEvent) {
        const eventDetailsResponse = yield call(api.postEventDetails, eventId, {
          date: eventDate,
          event_type: eventType,
        })

        const eventDetails = api.onStatus({
          OK: ({json}) => json,
          403: () => {
            throw new Error('CSRF Token Invalid')
          },
          DEFAULT: ({statusText}) => {
            throw new Error(`Could not post event details: ${statusText}`)
          },
        })(eventDetailsResponse)

        yield put(
          eventsActions.updateCurrentEvent({
            date: eventDetails.date,
            type: eventDetails.eventType,
            name: eventDetails.name,
          }),
        )
      }
    }

    const showroomSlug = yield select(showroomApptSelectors.showroomSlug)
    yield call(history.push, `${URLS.SHOWROOMS}/${showroomSlug}/bookappointment-thankyou`)
  } catch (e) {
    yield put(userInteractionActions.openErrorModal())
  }
}

export function* createAppointmentSuccessSaga() {
  yield takeLatest(showroomApptActions.CREATE_APPOINTMENT_SUCCESS, appointmentCreationSuccessWorker)
}

export function* trackFailedAppointmentFlowWorker(error) {
  yield call(showroomTrack.blockedAppointmentFlow, error.type)
}

export function* createAppointmentFailureSaga() {
  yield takeLatest(
    [
      showroomApptActions.CREATE_APPOINTMENT_FAILURE,
      showroomApptActions.GET_SHOWROOM_APPOINTMENT_SERVICES_FAILURE,
      showroomApptActions.GET_SHOWROOM_APPOINTMENT_SCHEDULE_FAILURE,
      showroomApptActions.GET_SHOWROOM_OPEN_TIMES_FAILURE,
    ],
    trackFailedAppointmentFlowWorker,
  )
}

// Get Appointment
// ----------------------------------------------------------------------------

export function* getAppointmentWorker({payload: {id, obfuscatedId}}) {
  const beginAt = yield select(showroomApptSelectors.appointmentTimeslot)
  yield call(cancelTrack.cancelAppointmentPageLoaded, differenceInDays(beginAt, new Date()))

  try {
    yield put(showroomApptActions.getAppointmentPending())

    const response = yield call(api.getAppointment, id, obfuscatedId)

    const result = api.onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Unable to fetch appointment: ${statusText}`)
      },
    })(response)
    yield put(showroomApptActions.getAppointmentSuccess(result))
  } catch (e) {
    yield put(showroomApptActions.getAppointmentFailure(new Error('Unable to fetch appointment.')))
  }
}

export function* getAppointmentSaga() {
  yield takeLatest(showroomApptActions.GET_APPOINTMENT, getAppointmentWorker)
}

// Cancel Appointment
// ----------------------------------------------------------------------------

export function* cancelAppointmentWorker({payload: {id, obfuscatedId}}) {
  try {
    const beginAt = yield select(showroomApptSelectors.appointmentTimeslot)

    yield put(showroomApptActions.cancelAppointmentPending())

    const response = yield call(api.cancelAppointment, id, obfuscatedId)

    api.onStatus({
      OK: noop,
      DEFAULT: ({statusText}) => {
        throw new Error(`Unable to cancel appointment: ${statusText}`)
      },
    })(response)

    yield call(cancelTrack.cancelledAppointment, differenceInDays(beginAt, new Date()))

    yield put(showroomApptActions.cancelAppointmentSuccess())
  } catch (e) {
    yield put(
      showroomApptActions.cancelAppointmentFailure(new Error('Unable to cancel appointment.')),
    )
  }
}

export function* cancelAppointmentSaga() {
  yield takeLatest(showroomApptActions.CANCEL_APPOINTMENT, cancelAppointmentWorker)
}
